import { alert } from '@/services/alert';
import { useMedplum } from '@medplum/react';
import { useCallback } from 'react';
import { useTimelineContext } from './useTimelineContext';
import { useRelatedPersonList } from '../context-hooks/useRelatedPersonList';
import {
  createRelatedPerson as createRelatedPersonService,
  updateRelatedPerson as updateRelatedPersonService,
  deleteRelatedPerson as deleteRelatedPersonService,
} from '../PatientSummary/RelatedContact/services';
import { RelatedContactFormData } from '../PatientSummary/RelatedContact/forms/types';

function formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'issue' in error) {
    return JSON.stringify(error);
  }
  return 'Unknown error';
}

export interface UseRelatedPersonManagementResult {
  createRelatedPerson: (contactData: RelatedContactFormData) => Promise<void>;
  updateRelatedPerson: (relatedPersonId: string, contactData: RelatedContactFormData) => Promise<void>;
  deleteRelatedPerson: (relatedPersonId: string) => Promise<void>;
}

export function useRelatedPersonManagement(): UseRelatedPersonManagementResult {
  const medplum = useMedplum();
  const { patientContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const { refreshRelatedPersonList } = useRelatedPersonList(patient);

  const createRelatedPerson = useCallback(
    async (contactData: RelatedContactFormData) => {
      if (!patient) return;

      try {
        await createRelatedPersonService(medplum, { patient, contactData });
        refreshRelatedPersonList();
      } catch (error) {
        alert.error(`Failed to create related contact: ${formatError(error)}`);
      }
    },
    [patient, medplum, refreshRelatedPersonList]
  );

  const updateRelatedPerson = useCallback(
    async (relatedPersonId: string, contactData: RelatedContactFormData) => {
      if (!patient) return;

      try {
        await updateRelatedPersonService(medplum, { relatedPersonId, contactData });
        refreshRelatedPersonList();
      } catch (error) {
        alert.error(`Failed to update related contact: ${formatError(error)}`);
      }
    },
    [patient, medplum, refreshRelatedPersonList]
  );

  const deleteRelatedPerson = useCallback(
    async (relatedPersonId: string) => {
      try {
        await deleteRelatedPersonService(medplum, relatedPersonId);
        refreshRelatedPersonList();
      } catch (error) {
        alert.error(`Failed to remove related contact: ${formatError(error)}`);
      }
    },
    [medplum, refreshRelatedPersonList]
  );

  return {
    createRelatedPerson,
    updateRelatedPerson,
    deleteRelatedPerson,
  };
}
